import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

function MobileSurvey() {
  const [survey, setSurvey] = useState(null);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState({});
  const { company_username } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await axios.get(
          `https://zola.technology/api/company/get/${company_username}`
        );
        setCompany(res.data);
      } catch (err) {
        console.error("Failed to fetch company info", err);
      }
    };

    if (company_username) fetchCompany();
  }, [company_username]);

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const response = await axios.get(
          `https://zola.technology/api/company/${company_username}/surveys/survey/get-active/`
        );
        setSurvey(response.data);
      } catch (error) {
        console.error("Error fetching survey:", error);
        setError("Failed to load survey. Please try again.");
      }
    };
    fetchSurvey();
  }, [company_username]);

  const handleResponseChange = (questionId, value) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleCheckboxChange = (questionId, choice) => {
    setResponses((prev) => {
      const current = prev[questionId] || [];
      const updated = current.includes(choice)
        ? current.filter((c) => c !== choice)
        : [...current, choice];
      return { ...prev, [questionId]: updated };
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true); // Start loading

    const formattedAnswers = survey.questions.map((q) => {
      const response = responses[q.id];
      let formatted;
      if (q.question_type === "multi_choice") {
        formatted = response || [];
      } else if (q.question_type === "numeric_choice") {
        formatted = response !== undefined ? parseInt(response, 10) : 0;
      } else {
        formatted = response || "";
      }
      return { question_id: q.id, response: formatted };
    });

    try {
      await axios.post(
        `https://zola.technology/api/company/${company_username}/surveys/survey/get-responses/`,
        { survey_id: survey.id, answers: formattedAnswers }
      );

      await axios.patch(
        `https://zola.technology/api/company/${company_username}/surveys/survey/update-completed-count/?survey_id=${survey.id}`,
        {}
      );

      try {
        const couponResponse = await axios.post(
          `https://zola.technology/api/company/${company_username}/coupon/codes/generate/?applicable_area=survey`,
          {}
        );

        if (couponResponse.data) {
          localStorage.setItem(
            "coupon_data",
            JSON.stringify(couponResponse.data)
          );
          navigate("/redeem-coupon");
        } else {
          navigate(`/feedback/${company_username}/${company_username}`);
        }
      } catch (couponError) {
        if (
          couponError.response?.data?.detail ===
          "No valid unique coupons available."
        ) {
          navigate(`/feedback/${company_username}/${company_username}`);
        } else {
          console.error("Coupon error:", couponError);
          toast.error("An unexpected error occurred with coupon generation.");
        }
      }
    } catch (error) {
      console.error("Error submitting survey:", error);
      toast.error("Please complete all required questions.");
    } finally {
      setSubmitting(false); // Stop loading
    }
  };

  if (error) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading survey...</p>
      </div>
    );
  }
  return (
    <div className="min-h-screen font-outfit px-4 py-6 lg:bg-gray-100 flex justify-center">
      <div className="w-full md:max-w-[750px] bg-white p-6 rounded-2xl shadow-sm">
        <ToastContainer />

        {company && (
          <div className="mb-6 rounded-xl p-4 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
            {company.profile_image && (
              <img
                src={company.profile_image}
                alt={company.company_name}
                className="w-16 h-16 rounded-full object-cover border border-gray-300 shadow-sm"
              />
            )}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {company.preferred_business_name || company.company_name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {survey.title || "Customer Feedback Survey"}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-10">
          {survey.questions.map((question, index) => (
            <div key={question.id} className="mb-4">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                {index + 1}. {question.question_text}
              </h2>

              {question.question_type === "paragraph" && (
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-4 py-3"
                  rows={4}
                  placeholder="Type your answer..."
                  value={responses[question.id] || ""}
                  onChange={(e) =>
                    handleResponseChange(question.id, e.target.value)
                  }
                />
              )}

              {question.question_type === "single_choice" && (
                <div className="flex flex-col gap-3">
                  {Object.entries(question.choices).map(([key, value]) => {
                    const selected = responses[question.id] === value;
                    return (
                      <label
                        key={key}
                        className={`w-full px-4 py-3 rounded-lg border text-left text-sm cursor-pointer transition ${
                          selected
                            ? "border-blue-500 text-blue-700 bg-blue-50"
                            : "border-gray-300 hover:border-blue-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name={`question-${question.id}`}
                          value={value}
                          checked={selected}
                          onChange={() =>
                            handleResponseChange(question.id, value)
                          }
                          className="hidden"
                        />
                        {value}
                      </label>
                    );
                  })}
                </div>
              )}

              {question.question_type === "multi_choice" && (
                <div className="flex flex-col gap-3">
                  {Object.entries(question.choices).map(([key, value]) => {
                    const selected = responses[question.id]?.includes(value);
                    return (
                      <label
                        key={key}
                        className={`w-full px-4 py-3 rounded-lg border text-left text-sm cursor-pointer transition ${
                          selected
                            ? "border-blue-500 text-blue-700 bg-blue-50"
                            : "border-gray-300 hover:border-blue-300"
                        }`}
                      >
                        <input
                          type="checkbox"
                          value={value}
                          checked={selected}
                          onChange={() =>
                            handleCheckboxChange(question.id, value)
                          }
                          className="hidden"
                        />
                        {value}
                      </label>
                    );
                  })}
                </div>
              )}

              {question.question_type === "numeric_choice" &&
                (() => {
                  const min = Math.min(
                    ...Object.values(question.choices).map(Number)
                  );
                  const max = Math.max(
                    ...Object.values(question.choices).map(Number)
                  );
                  const currentValue = responses[question.id]
                    ? Number(responses[question.id])
                    : min;
                  const fillPercentage =
                    ((currentValue - min) / (max - min)) * 100;

                  return (
                    <div className="w-full mt-2">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">
                          Value: {currentValue}
                        </span>
                      </div>
                      <input
                        type="range"
                        min={min}
                        max={max}
                        step="1"
                        value={currentValue}
                        onChange={(e) =>
                          handleResponseChange(question.id, e.target.value)
                        }
                        className="w-full h-3 rounded-full appearance-none cursor-pointer
                        [&::-webkit-slider-thumb]:appearance-none
                        [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6
                        [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:shadow-md
                        [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:w-6
                        [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-600"
                        style={{
                          background: `linear-gradient(to right, #3b82f6 ${fillPercentage}%, #e5e7eb ${fillPercentage}%)`,
                        }}
                      />
                      <div className="flex justify-between text-xs text-gray-600 mt-2">
                        {Object.values(question.choices).map((val, idx) => (
                          <span key={idx} className="w-4 text-center">
                            {val}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
            </div>
          ))}
        </div>

        <div className="mt-10">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`w-full py-3 rounded-full font-medium transition ${
              submitting
                ? "bg-blue-300 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MobileSurvey;
